import React, { useEffect, useMemo, useState } from "react";
import SalesBarChart from "./chart/SalesBarChart";
import PurchaseBarChart from "./chart/PurchaseBarChart";
import ExpenseChart from "./chart/ExpenseChart";
import OtherIncomeChart from "./chart/OtherIncomeChart";
import { PostData } from "../../service/postData";
import CashInHandChart from "./chart/CashInHandChart";
import SaleOutstandingBar from "./chart/SaleOutstandingBar";
import PurchaseOutstandingBar from "./chart/PurchaseOutstandingBar";
import SalesOrderChart from "./chart/SalesOrderChart";
import PurchasesOrderChart from "./chart/PurchasesOrderChart";
import { Loader } from "@progress/kendo-react-indicators";
import DatePicker from "react-datepicker";
import moment from "moment";
import "./chart/YearPicker.css";
import WillGiveChart from "./chart/WillGiveChart";
import WillGetChart from "./chart/WillGetChart";

const formatINR = (val) =>
  `₹${Number(val || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const sumSeries = (arr) =>
  Array.isArray(arr) ? arr.reduce((s, r) => s + (Number(r.value) || 0), 0) : 0;

export default function DashboardChart({ viewMode = "chart", onViewChange }) {
  const [companyId, setCompanyId] = useState(null);
  const [businessId, setBusinessId] = useState(null);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [saleTotalAmount, setSaleTotalAmount] = useState(0);
  const [purchaseTotalAmount, setPurchaseTotalAmount] = useState(0);
  const [saleChartData, setSaleChartData] = useState([]);
  const [purchaseChartData, setPurchaseChartData] = useState([]);
  const [expenseChartData, setExpenseChartData] = useState([]);
  const [incomeChartData, setIncomeChartData] = useState([]);
  const [cashInHandChartData, setCashInHandChartData] = useState([]);
  const [willGiveChartData, setWillGiveChartData] = useState([]);
  const [willGetChartData, setWillGetChartData] = useState([]);
  const [saleOutstandingData, setSaleOutstandingData] = useState({});
  const [purchaseOutstandingData, setPurchaseOutstandingData] = useState({});
  const [saleOrderChartData, setSaleOrderChartData] = useState([]);
  const [purchaseOrderChartData, setPurchaseOrderChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async (yearToUse) => {
      try {
        let _companySession = localStorage.getItem("vs_UserCompanySession");
        if (_companySession != undefined || _companySession != null) {
          _companySession = JSON.parse(_companySession);
          let _businessId = _companySession.business_id;
          let _company_id = _companySession.company_id;
          setCompanyId(_company_id);
          setBusinessId(_businessId);
          getTransactionDetails(_company_id, _businessId, yearToUse);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData(new Date().getFullYear());
  }, []);

  const getTransactionDetails = (company_id, business_id, year) => {
    setLoading(true);
    const requestData = JSON.stringify({ business_id, company_id, year });
    PostData(global.userBusinessDashboardCountList1, requestData, "POST")
      .then((result) => {
        if (result.success) {
          const responseJson = result.response || {};
          const saleData = responseJson.saleData || {};
          const purchaseData = responseJson.purchaseData || {};
          const cashInHandData = responseJson.cashInHandData || {};
          const saleOutstanding = responseJson.saleOutstandingData || {};
          const purchaseOutstanding = responseJson.purchaseOutstandingData || {};

          setSaleTotalAmount(saleData.totalAmount || 0);
          setPurchaseTotalAmount(purchaseData.totalAmount || 0);
          setSaleChartData(saleData.chartData || []);
          setPurchaseChartData(purchaseData.chartData || []);
          setExpenseChartData(responseJson.expenseData || []);
          setIncomeChartData(responseJson.otherIncomeData || []);
          setCashInHandChartData(cashInHandData.chartData || []);
          setWillGiveChartData(responseJson.youWillGiveData || []);
          setWillGetChartData(responseJson.youWillGetData || []);
          setSaleOutstandingData(saleOutstanding || {});
          setPurchaseOutstandingData(purchaseOutstanding || {});
          setSaleOrderChartData(responseJson.saleOrderData || []);
          setPurchaseOrderChartData(responseJson.purchaseOrderData || []);
        }
      })
      .finally(() => setLoading(false));
  };

  const kpis = useMemo(
    () => [
      {
        label: "Total Sales",
        value: formatINR(saleTotalAmount),
        accent: "#3b82f6",
        icon: "si si-basket",
      },
      {
        label: "Total Purchases",
        value: formatINR(purchaseTotalAmount),
        accent: "#8b5cf6",
        icon: "si si-bag",
      },
      {
        label: "You'll Get",
        value: formatINR(sumSeries(willGetChartData)),
        accent: "#10b981",
        icon: "si si-arrow-down",
      },
      {
        label: "You'll Give",
        value: formatINR(sumSeries(willGiveChartData)),
        accent: "#ef4444",
        icon: "si si-arrow-up",
      },
    ],
    [saleTotalAmount, purchaseTotalAmount, willGetChartData, willGiveChartData]
  );

  const renderHeader = () => (
    <div style={styles.hero}>
      <div style={styles.heroLeft}>
        <div style={styles.heroTitleRow}>
          <span style={styles.heroBadge}>FY {currentYear}</span>
          <h2 style={styles.pageTitle}>Dashboard Analytics</h2>
        </div>
        <p style={styles.pageSubtitle}>
          A quick snapshot of your business performance for the selected year.
        </p>
      </div>

      <div style={styles.heroRight}>
        <div style={styles.segmented} role="tablist" aria-label="View mode">
          <button
            type="button"
            style={{
              ...styles.segmentedBtn,
              ...(viewMode === "chart" ? styles.segmentedBtnActive : {}),
            }}
            onClick={() => onViewChange && onViewChange("chart")}
          >
            <i className="si si-pie-chart" style={{ marginRight: 6 }}></i>
            Chart
          </button>
          <button
            type="button"
            style={{
              ...styles.segmentedBtn,
              ...(viewMode === "normal" ? styles.segmentedBtnActive : {}),
            }}
            onClick={() => onViewChange && onViewChange("normal")}
          >
            <i className="si si-list" style={{ marginRight: 6 }}></i>
            Normal
          </button>
        </div>

        <div className="year-picker-wrapper" style={styles.yearPickerWrap}>
          <DatePicker
            selected={moment(currentYear, "YYYY").toDate()}
            onChange={(date) => {
              const selectedYear = date.getFullYear();
              setCurrentYear(selectedYear);
              getTransactionDetails(companyId, businessId, selectedYear);
            }}
            dateFormat="yyyy"
            showYearPicker
            yearItemNumber={15}
            placeholderText="Select Year"
            dropdownMode="select"
            className="custom-datepicker"
            customInput={
              <div className="custom-input-datepicker" style={styles.yearInput}>
                <i
                  className="si si-calendar"
                  style={{ marginRight: 8, color: "#6b7280" }}
                ></i>
                <span style={{ fontWeight: 600 }}>{currentYear}</span>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={styles.container}>
        {renderHeader()}
        <div style={styles.loadingCard}>
          <Loader type="pulsing" size="large" />
          <p style={styles.loadingText}>
            Data is being processed, please wait…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {renderHeader()}

      <div style={styles.kpiGrid}>
        {kpis.map((k) => (
          <div key={k.label} style={styles.kpiCard}>
            <div
              style={{
                ...styles.kpiIconWrap,
                backgroundColor: `${k.accent}1a`,
                color: k.accent,
              }}
            >
              <i className={k.icon}></i>
            </div>
            <div style={styles.kpiTextWrap}>
              <span style={styles.kpiLabel}>{k.label}</span>
              <span style={styles.kpiValue}>{k.value}</span>
            </div>
            <span style={{ ...styles.kpiAccent, backgroundColor: k.accent }} />
          </div>
        ))}
      </div>

      <Section title="Outstanding & Receivables" accent="#10b981">
        <div style={styles.gridTwoColumns}>
          <WillGiveChart data={willGiveChartData} />
          <WillGetChart data={willGetChartData} />
        </div>
      </Section>

      <Section title="Sales & Purchase Analytics" accent="#3b82f6">
        <div style={styles.gridTwoColumns}>
          <SalesBarChart data={saleChartData} totalAmount={saleTotalAmount} />
          <PurchaseBarChart
            data={purchaseChartData}
            totalAmount={purchaseTotalAmount}
          />
        </div>
      </Section>

      <Section title="Outstanding Analytics" accent="#f59e0b">
        <div style={styles.gridTwoColumns}>
          <SaleOutstandingBar
            title="Sale Outstanding"
            total={saleOutstandingData.totalAmount || 0}
            values={saleOutstandingData.values || []}
            labels={saleOutstandingData.labels || []}
            colors={["#10b981", "#EF4444"]}
          />
          <PurchaseOutstandingBar
            title="Purchase Outstanding"
            total={purchaseOutstandingData.totalAmount || 0}
            values={purchaseOutstandingData.values || []}
            labels={purchaseOutstandingData.labels || []}
            colors={["#3B82F6", "#EF4444"]}
          />
        </div>
      </Section>

      <Section title="Orders Overview" accent="#8b5cf6">
        <div style={styles.gridTwoColumns}>
          <SalesOrderChart data={saleOrderChartData} />
          <PurchasesOrderChart data={purchaseOrderChartData} />
        </div>
      </Section>

      <Section title="Financial Overview" accent="#0ea5e9">
        <div style={styles.gridThreeColumns}>
          <CashInHandChart data={cashInHandChartData} />
          <ExpenseChart data={expenseChartData} />
          <OtherIncomeChart data={incomeChartData} />
        </div>
      </Section>
    </div>
  );
}

const Section = ({ title, accent, children }) => (
  <div style={styles.sectionWrapper}>
    <div style={styles.sectionHeader}>
      <span style={{ ...styles.sectionDot, backgroundColor: accent }} />
      <h3 style={styles.sectionTitle}>{title}</h3>
    </div>
    {children}
  </div>
);

const styles = {
  container: {
    padding: "24px 28px 40px",
    backgroundColor: "#f4f6fb",
    minHeight: "100vh",
  },
  hero: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "16px",
    padding: "22px 26px",
    background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
    border: "1px solid #e5e7eb",
    borderRadius: "14px",
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
    marginBottom: "22px",
  },
  heroLeft: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    minWidth: 260,
  },
  heroTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  heroBadge: {
    fontSize: "12px",
    fontWeight: 600,
    letterSpacing: "0.5px",
    color: "#2563eb",
    backgroundColor: "#dbeafe",
    padding: "4px 10px",
    borderRadius: "999px",
    textTransform: "uppercase",
  },
  pageTitle: {
    fontSize: "26px",
    fontWeight: 700,
    color: "#0f172a",
    margin: 0,
    letterSpacing: "-0.01em",
  },
  pageSubtitle: {
    fontSize: "13px",
    color: "#64748b",
    margin: 0,
  },
  heroRight: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  segmented: {
    display: "inline-flex",
    padding: "4px",
    backgroundColor: "#f1f5f9",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
  },
  segmentedBtn: {
    border: "none",
    background: "transparent",
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#64748b",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  segmentedBtnActive: {
    backgroundColor: "#ffffff",
    color: "#2563eb",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.08)",
  },
  yearPickerWrap: {
    display: "inline-block",
  },
  yearInput: {
    display: "inline-flex",
    alignItems: "center",
    padding: "8px 14px",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    cursor: "pointer",
    minWidth: "110px",
    fontSize: "13px",
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "16px",
    marginBottom: "26px",
  },
  kpiCard: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "18px 20px",
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
    overflow: "hidden",
  },
  kpiAccent: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: "4px",
  },
  kpiIconWrap: {
    width: 44,
    height: 44,
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    flexShrink: 0,
  },
  kpiTextWrap: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
  },
  kpiLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  kpiValue: {
    fontSize: "18px",
    fontWeight: 700,
    color: "#0f172a",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  sectionWrapper: {
    marginBottom: "28px",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "14px",
  },
  sectionDot: {
    width: 8,
    height: 22,
    borderRadius: "4px",
    display: "inline-block",
  },
  sectionTitle: {
    fontSize: "17px",
    fontWeight: 700,
    color: "#0f172a",
    margin: 0,
    letterSpacing: "-0.01em",
  },
  gridTwoColumns: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 500px), 1fr))",
    gap: "20px",
  },
  gridThreeColumns: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 350px), 1fr))",
    gap: "20px",
  },
  loadingCard: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "12px",
    padding: "60px 20px",
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
  },
  loadingText: {
    margin: 0,
    color: "#64748b",
    fontSize: "14px",
  },
};
